import { buildDiyStepAssetCatalogFromFormSettings } from '@/lib/diyStepCatalogSanitize';

/** Filter DevTools console with: diy_playbook_clone */
export const DIY_PLAYBOOK_CLONE_LOG = '[diy_playbook_clone]';

function positionalKeyStatus(key, count, kind) {
  if (key == null || key === '') return 'empty';
  const s = String(key).trim();
  let re;
  if (kind === 'kpi') re = /^kpi_(\d+)$/;
  else if (kind === 'hook') re = /^hook_(\d+)$/;
  else if (kind === 'case_study') re = /^case_study_(\d+)$/;
  else return 'unknown_kind';
  const m = s.match(re);
  if (!m) return 'non_positional_key';
  const i = parseInt(m[1], 10);
  if (!Number.isInteger(i) || i < 0 || i >= count) return 'not_in_catalog';
  return 'ok';
}

function productLinkKeyStatuses(keys, catalog) {
  const nameSet = catalog.productLinkNames;
  const alias = catalog.productLinkAliasToCanonical;
  const arr = Array.isArray(keys) ? keys : [];
  if (arr.length === 0) return [];
  if (nameSet.size === 0) {
    return arr.map((k) => ({ key: String(k), status: 'catalog_empty' }));
  }
  return arr.map((k) => {
    const t = String(k ?? '').trim();
    if (!t) return { key: t, status: 'empty' };
    const canon = alias.get(t) ?? (nameSet.has(t) ? t : null);
    return { key: t, status: canon != null ? 'ok' : 'not_in_catalog', canonical: canon };
  });
}

/**
 * After load/clone: verify each step's asset refs resolve against filter-options
 * (owner catalog = personalization + admin_atoms for current user).
 *
 * @param {unknown[]} steps
 * @param {Record<string, unknown>|undefined} settings — filter-options.settings
 */
export function auditDiyPlaybookStepAssets(steps, settings) {
  const catalog = buildDiyStepAssetCatalogFromFormSettings(settings);
  if (!catalog) return null;

  const perStep = (steps || []).map((step, index) => {
    const order = step?.order ?? index + 1;
    return {
      order,
      kpiKey: step?.kpiKey ?? null,
      kpiStatus: positionalKeyStatus(step?.kpiKey, catalog.kpiCount, 'kpi'),
      hookKey: step?.hookKey ?? null,
      hookStatus: positionalKeyStatus(step?.hookKey, catalog.hookCount, 'hook'),
      caseStudyKey: step?.caseStudyKey ?? null,
      caseStudyStatus: positionalKeyStatus(
        step?.caseStudyKey,
        catalog.caseStudyCount,
        'case_study',
      ),
      productLinkKeys: step?.productLinkKeys ?? [],
      productLinkStatuses: productLinkKeyStatuses(step?.productLinkKeys, catalog),
      educationalResourceKeys: step?.educationalResourceKeys ?? [],
    };
  });

  const missing = [];
  for (const row of perStep) {
    if (row.kpiStatus === 'not_in_catalog' || row.kpiStatus === 'non_positional_key') {
      missing.push({ order: row.order, field: 'kpiKey', status: row.kpiStatus, key: row.kpiKey });
    }
    if (row.hookStatus === 'not_in_catalog' || row.hookStatus === 'non_positional_key') {
      missing.push({ order: row.order, field: 'hookKey', status: row.hookStatus, key: row.hookKey });
    }
    if (row.caseStudyStatus === 'not_in_catalog' || row.caseStudyStatus === 'non_positional_key') {
      missing.push({
        order: row.order,
        field: 'caseStudyKey',
        status: row.caseStudyStatus,
        key: row.caseStudyKey,
      });
    }
    for (const pl of row.productLinkStatuses) {
      if (pl.status === 'not_in_catalog' || pl.status === 'catalog_empty') {
        missing.push({ order: row.order, field: 'productLinkKeys', ...pl });
      }
    }
  }

  const eduCount = Array.isArray(settings?.educationalResources)
    ? settings.educationalResources.length
    : 0;

  return {
    catalogCounts: {
      kpis: catalog.kpiCount,
      hooks: catalog.hookCount,
      caseStudies: catalog.caseStudyCount,
      productLinks: catalog.productLinkNames.size,
      educationalResources: eduCount,
      products: Array.isArray(settings?.products) ? settings.products.length : 0,
    },
    perStep,
    allStepAssetsResolvable: missing.length === 0,
    missingReferences: missing,
  };
}

/**
 * @param {unknown} data — POST /duplicate JSON body
 */
export function summarizeDiyCloneResponse(data) {
  if (!data || typeof data !== 'object') {
    return { newPlaybookId: null, rawType: typeof data };
  }
  const pb = /** @type {Record<string, unknown>} */ (data.playbook ?? data);
  const steps = pb?.steps;
  const assetsBundled = data.assets_bundled ?? data.assetsBundled ?? null;
  return {
    newPlaybookId: pb?.id ?? pb?.playbook_id ?? data.id ?? null,
    newOwnerUserId:
      pb?.owner_user_id ?? pb?.user_id ?? pb?.userId ?? data.user_id ?? null,
    name: pb?.name ?? pb?.playbook_name ?? null,
    stepCount: Array.isArray(steps) ? steps.length : undefined,
    /** From duplicate API body only (may be absent until BE implements). */
    apiSourcePlaybookId:
      data.source_playbook_id ?? data.sourcePlaybookId ?? data.source_id ?? null,
    apiAssetsBundled: assetsBundled,
    assetsBundledReportedByApi: assetsBundled != null,
    apiSuccess: data.success,
    responseTopLevelKeys: Object.keys(data),
  };
}

/**
 * @param {string} phase — start | success | http_error | network_error | invalid_context
 * @param {Record<string, unknown>} detail
 */
export function logDiyPlaybookClone(phase, detail = {}) {
  const payload = { phase, at: new Date().toISOString(), ...detail };
  if (phase === 'http_error' || phase === 'network_error' || phase === 'invalid_context') {
    console.warn(DIY_PLAYBOOK_CLONE_LOG, payload);
    return;
  }
  console.log(DIY_PLAYBOOK_CLONE_LOG, payload);

  if (phase === 'success') {
    const bundled = detail.assetsBundled ?? detail.apiAssetsBundled;
    console.log(`${DIY_PLAYBOOK_CLONE_LOG} clone_success_summary`, {
      sourcePlaybookId: detail.sourcePlaybookId,
      newPlaybookId: detail.newPlaybookId,
      newOwnerUserId: detail.newOwnerUserId,
      stepCount: detail.stepCount,
      assetsBundled: bundled ?? null,
      assetsBundledReportedByApi: detail.assetsBundledReportedByApi === true,
      hint:
        bundled == null
          ? 'BE did not return assets_bundled — use asset_catalog_audit after editor load to verify catalog'
          : 'See assetsBundled object from API for kpis/hooks/links copied count',
    });
  }

  if (phase === 'asset_catalog_audit') {
    const ok = detail.allStepAssetsResolvable === true;
    const counts = detail.catalogCounts ?? {};
    const picker = detail.pickerVisibleForSelection ?? {};
    const summary = {
      verdict: ok ? 'PASS — step asset keys resolve in your catalog' : 'FAIL — some step refs missing from catalog',
      playbookId: detail.playbookId,
      allStepAssetsResolvable: detail.allStepAssetsResolvable,
      missingCount: Array.isArray(detail.missingReferences)
        ? detail.missingReferences.length
        : 0,
      catalogCounts: counts,
      pickerVisibleForSelection: picker,
      selectedProductKeys: detail.selectedProductKeys,
    };
    if (ok) {
      console.log(`${DIY_PLAYBOOK_CLONE_LOG} asset_audit_summary`, summary);
    } else {
      console.warn(`${DIY_PLAYBOOK_CLONE_LOG} asset_audit_summary`, summary, {
        missingReferences: detail.missingReferences,
        perStep: detail.perStep,
      });
    }
  }
}
